import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
} from "react";
import api from "../api/axios";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isQRSession, setIsQRSession] = useState(false);
	
	const loadUser = useCallback(async () => {
		const token = localStorage.getItem("cff_token");
		if (!token) {
			setLoading(false);
			return;
		}
		try {
			const { data } = await api.get("/auth/me");
			setUser(data);
		} catch {
			localStorage.removeItem("cff_token");
		} finally {
			setLoading(false);
		}
	}, []);
	
	useEffect(() => {
		loadUser();
	}, [loadUser]);
	
	// ── login ────────────────────────────────────────────────────────────────
	// Returns one of:
	//   { status: "mfaRequired", mfaToken, firstName }
	//   { status: "totpSetupRequired", token, user }
	//   { status: "ok", user }
	const login = async (email, password) => {
		const { data } = await api.post("/auth/login", { email, password });
		
		if (data.mfaRequired) {
			return {
				status: "mfaRequired",
				mfaToken: data.mfaToken,
				firstName: data.firstName,
			};
		}
		
		if (data.requiresTotpSetup) {
			// Store the setup-scoped token so the TOTP setup page can call
			// the setup endpoints.
			localStorage.setItem("cff_token", data.token);
			setUser(data.user);
			setIsQRSession(false);
			return { status: "totpSetupRequired", user: data.user };
		}
		
		localStorage.setItem("cff_token", data.token);
		setUser(data.user);
		setIsQRSession(false);
		return { status: "ok", user: data.user };
	};
	
	// ── verifyMfa ────────────────────────────────────────────────────────────
	const verifyMfa = async (mfaToken, code) => {
		const { data } = await api.post("/auth/totp/verify-login", {
			mfaToken,
			code,
		});
		localStorage.setItem("cff_token", data.token);
		setUser(data.user);
		setIsQRSession(false);
		return data.user;
	};
	
	// ── register ─────────────────────────────────────────────────────────────
	const register = async (fields) => {
		const { data } = await api.post("/auth/register", fields);
		// After registration, always require TOTP setup
		localStorage.setItem("cff_token", data.token);
		setUser(data.user);
		setIsQRSession(false);
		return { status: "totpSetupRequired", user: data.user };
	};
	
	// ── verifyPin (QR flow — no MFA) ─────────────────────────────────────────
	const verifyPin = async (accountNumber, pin) => {
		const { data } = await api.post("/auth/verify-pin", {
			accountNumber,
			pin,
		});
		localStorage.setItem("cff_token", data.token);
		setUser(data.user);
		setIsQRSession(true);
		return data.user;
	};
	
	const logout = () => {
		localStorage.removeItem("cff_token");
		setUser(null);
		setIsQRSession(false);
	};
	
	const refreshUser = async () => {
		const { data } = await api.get("/auth/me");
		setUser(data);
	};
	
	return (
		<AuthContext.Provider
		value={{
			user,
			loading,
			isQRSession,
			login,
			verifyMfa,
			register,
			verifyPin,
			logout,
			refreshUser,
		}}
		>
		{children}
		</AuthContext.Provider>
	);
}

export const useAuth = () => useContext(AuthContext);
